import React, { useCallback, useContext, useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { mediaQueries } from '@general/breakpoints';

import MailSVG from '@icons/MailIcon/MailSVG';
import InboxConversationItem from './InboxConversationItem';
import InboxConversationItemVisibility from './InboxConversationItemVisibility';
import { ConversationsContext } from '../../../context/ConversationsProvider';
import { ConversationSearchContext } from './providers/ConversationSearchProvider';
import { BodySmall, ButtonNoStyles } from '@react/styledComponents';
import LoadingInline from '@molecules/LoadingInline';
import colors from '@general/colors';

export const InboxConversationListStyled = styled.div`
    overflow-y: auto;
    padding: 10px 20px 10px 10px;
    height: 250px;
    @media ${mediaQueries.md} {
        height: 495px;
    }
`;

const LoadMoreButtonStyled = styled(ButtonNoStyles)`
    color: ${colors.primary};
`;

const InboxConversationList = () => {
    const {
        conversations,
        currentConversation,
        fetchFirstPageConversations,
        fetchPaginatedConversations,
        hasMoreConversations,
        selectConversation,
    } = useContext(ConversationsContext);
    const { conversationSearchQuery } = useContext(ConversationSearchContext);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const pageSize = 50;

    const handlePagination = async () => {
        setIsLoading(true);
        const pageNumber = currentPage + 1;
        setCurrentPage(pageNumber);
        await fetchPaginatedConversations(pageNumber, pageSize);
        setIsLoading(false);
    };

    const memoizedFetchFirstPageConversations = useCallback(fetchFirstPageConversations, []);

    useEffect(() => {
        const loadConversations = async () => {
            await memoizedFetchFirstPageConversations();
        };

        loadConversations();
    }, [memoizedFetchFirstPageConversations]);

    // select first conversation if there is no current conversation
    useEffect(() => {
        if (!conversations || conversations.length === 0 || currentConversation) return;
        selectConversation(conversations[0]);
    }, [conversations, currentConversation, selectConversation]);

    if (!conversations) {
        return null;
    }

    if (conversations.length === 0) {
        return (
            <>
                <p className="font-size-sm" style={{ lineHeight: '1.6rem' }}>
                    After you message someone, your conversations will appear here.
                </p>
                <p className="font-size-sm" style={{ lineHeight: '1.6rem' }}>
                    Start a conversation by clicking on the{' '}
                    <span style={{ marginRight: '5px', marginLeft: '3px', display: 'inline-block' }}>
                        <MailSVG />
                    </span>
                    in a member's card.
                </p>
            </>
        );
    }

    const handleClickItem = conversation => {
        selectConversation(conversation);
    };

    const renderDisplayNumberText = numberOfConversations => {
        if (numberOfConversations === 1) return `Displaying 1 conversation`;
        return `Displaying ${numberOfConversations} conversations`;
    };

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const visibleConversations = conversations?.slice(0, endIndex);
    const showLoadMoreButton = hasMoreConversations;

    return (
        <InboxConversationListStyled className="fade-in-fast" onScroll={() => ReactTooltip.hide()}>
            {visibleConversations.map(conversation => (
                <InboxConversationItemVisibility
                    key={`converstation-list-item-${conversation.sid}`}
                    conversation={conversation}
                >
                    <InboxConversationItem
                        conversation={conversation}
                        isActive={conversation.sid === currentConversation?.sid}
                        onClickItem={handleClickItem}
                    />
                </InboxConversationItemVisibility>
            ))}
            {isLoading ? (
                <LoadingInline />
            ) : (
                <>
                    {!conversationSearchQuery && (
                        <BodySmall className="mb-1">{renderDisplayNumberText(visibleConversations.length)}</BodySmall>
                    )}

                    {showLoadMoreButton && (
                        <LoadMoreButtonStyled
                            onClick={e => {
                                e.preventDefault();
                                handlePagination();
                            }}
                            type="button"
                        >
                            Load More...
                        </LoadMoreButtonStyled>
                    )}
                </>
            )}
        </InboxConversationListStyled>
    );
};

export default InboxConversationList;
